// Use the no-conflict version of Rollbar, which is appropriate for our
// use-case since it won't interfere if the containing page also happens to use
// Rollbar.js:
// https://docs.rollbar.com/docs/javascript#section-using-inside-an-embedded-component
import Rollbar from 'rollbar/dist/rollbar.noconflict.umd';

// Based on https://github.com/getsentry/raven-js/blob/3.26.2/dist/plugins/vue.js
function formatComponentName(vm) {
  if (vm.$root === vm) {
    return 'root instance';
  }
  /* eslint-disable no-underscore-dangle */
  const name = vm._isVue ? vm.$options.name || vm.$options._componentTag : vm.name;
  return (
    (name ? `component <${name}>` : 'anonymous component')
    + (vm._isVue && vm.$options.__file ? ` at ${vm.$options.__file}` : '')
  );
  /* eslint-enable no-underscore-dangle */
}

const RollbarPlugin = {
  install(Vue) {
    const { hostname } = window.location;

    // Only enable Rollbar reporting on staging and production for our domains,
    // NRCan's domains, and a couple Coalition domains (to ensure external
    // widget embeds are working).
    //
    // While it might be ideal to also enable it for all external domains,
    // there's too many odd things happening on external site's javascript that
    // cause other random errors to be reported (even with the captureUncaught
    // and captureUnahdneldRejections disabled below). But between our site,
    // NRCan's, and a couple coalitions, we should still see most real issues
    // any external widget would see.
    let enabled = false;
    if (process.env.VUE_APP_ENV === 'staging' || process.env.VUE_APP_ENV === 'production') {
      if (hostname.includes('afdc.energy.gov') || hostname.includes('afdc-stage.tada-stage.nrel.gov') || hostname.includes('natural-resources.canada.ca') || hostname.includes('ressources-naturelles.canada.ca') || hostname.includes('nrcan.gc.ca') || hostname.includes('rncan.gc.ca') || hostname.includes('cleanfuelsohio.org') || hostname.includes('vacleancities.org')) {
        enabled = true;
      }
    }

    // Only capture uncaught exceptions on our domains.
    //
    // While it might be nice to always capture uncaught exceptions for other
    // external embeds, since our widget lives directly on the other site
    // (rather than being isolated in an iframe), capture uncaught exceptions
    // also means we get a lot of random errors from unrelated javascript
    // issues on the external embedder's sites (including Canada's site, which
    // has other javascript issues on their page). So we'll ignore these and
    // assume if it's working on our site site, then we've dealt with most
    // widget/embed compatibility issues.
    let captureUncaught = false;
    let captureUnhandledRejections = false;
    if (hostname.includes('afdc.energy.gov') || hostname.includes('afdc-stage.tada-stage.nrel.gov')) {
      captureUncaught = true;
      captureUnhandledRejections = true;
    }

    // eslint-disable-next-line no-param-reassign, no-multi-assign
    Vue.prototype.$rollbar = Vue.$rollbar = new Rollbar({
      accessToken: '80bfc5dc3bc2451fad4a7df1eaddad0e',
      enabled,
      captureUncaught,
      captureUnhandledRejections,
      payload: {
        environment: process.env.VUE_APP_ENV,
      },
      // Since there's not a great concept of distinct error classes in most of
      // our javascript error handling, change the default fingerprinting
      // (grouping) approach to group by the error message instead (this
      // prevents all the client-side errors from getting grouped under a
      // single error in rollbar's interface).
      transform(payload) {
        if (payload && payload.body && payload.body.trace && payload.body.trace.exception && payload.body.trace.exception.description) {
          // eslint-disable-next-line no-param-reassign
          payload.fingerprint = payload.body.trace.exception.description;
        }
      },
    });

    // Errors throwing within Vue components may not bubble up to Rollbar due
    // to Vue's own error handling. So customize the Vue error handler to
    // integrate rollbar reporting. This is based on Sentry.js's Vue
    // integration:
    // https://github.com/getsentry/raven-js/blob/3.26.2/dist/plugins/vue.js
    const originalErrorHandler = Vue.config.errorHandler;
    // eslint-disable-next-line no-param-reassign
    Vue.config.errorHandler = function VueErrorHandler(error, vm, info, ...args) {
      const metadata = {};
      if (Object.prototype.toString.call(vm) === '[object Object]') {
        metadata.componentName = formatComponentName(vm);
        metadata.propsData = vm.$options.propsData;
      }

      if (typeof info !== 'undefined') {
        metadata.lifecycleHook = info;
      }
      Vue.$rollbar.error(error);

      if (typeof originalErrorHandler === 'function') {
        return originalErrorHandler.apply(this, error, vm, info, ...args);
      }

      if (typeof console !== 'undefined') {
        // eslint-disable-next-line no-console
        console.error(error);
      } else {
        throw error;
      }
    };
  },
};

export default RollbarPlugin;
